import { chain, concat, get, lowerCase, reduce } from 'lodash';
import applyTweak from '@/utilities/applyTweak';
function getMultiplier(className, myClasses, archetypeName, myArchetypes, castingType) {
    const myClass = myClasses.find(({ name, casterType }) => (name === className) && (casterType === castingType));
    const myArchetype = archetypeName && myArchetypes.find(({ name, casterType }) => (name === archetypeName) && (casterType === castingType));
    const classMultiplier = myClass ? myClass.casterRatio : 0;
    return myArchetype ? myArchetype.casterRatio : classMultiplier;
}
function getCastingLevel(rawCharacter, myClasses, myArchetypes, castingType) {
    if (castingType === 'Force' &&
        rawCharacter.classes.length === 1 &&
        rawCharacter.classes[0].name === 'Guardian' &&
        rawCharacter.classes[0].levels === 1)
        return 1;
    return rawCharacter.classes.reduce((acc, { name, archetype, levels }) => acc + (levels * getMultiplier(name, myClasses, archetype && archetype.name, myArchetypes, castingType) || 0), 0);
}
function getMaxPowerLevelAtClassLevel(classLevel, className, myClasses, archetypeName, myArchetypes, castingType) {
    const myClass = myClasses.find(({ name, casterType }) => (name === className) && (casterType === castingType));
    const myArchetype = archetypeName && myArchetypes.find(({ name, casterType }) => (name === archetypeName) && (casterType === castingType));
    if (myArchetype) {
        const leveledTable = myArchetype.leveledTable;
        if (leveledTable) {
            const maxPowerLevelValue = leveledTable[classLevel].find(({ key }) => key === 'Max Power Level');
            return maxPowerLevelValue ? parseInt(maxPowerLevelValue.value) : 0;
        }
    }
    return myClass ? parseInt(myClass.levelChanges[classLevel]['Max Power Level']) : 0;
}
function getMaxPowerLevel(rawCharacter, myClasses, consular, myArchetypes, castingType) {
    let maxPower = 0;
    if (rawCharacter.classes.length === 1) {
        // Use class or archetype table to determine max power level
        maxPower = getMaxPowerLevelAtClassLevel(rawCharacter.classes[0].levels, rawCharacter.classes[0].name, myClasses, rawCharacter.classes[0].archetype && rawCharacter.classes[0].archetype.name, myArchetypes, castingType);
    }
    else {
        // Use multiclass rules to determine max power level
        let maxPowerLevel = reduce(rawCharacter.classes, (acc, myClass) => {
            const potential = getMaxPowerLevelAtClassLevel(20, myClass.name, myClasses, myClass.archetype && myClass.archetype.name, myArchetypes, castingType);
            const levels = myClass.levels * potential / 9;
            return Math.max(levels, 0) + acc;
        }, 0);
        if (consular && maxPowerLevel > 0) {
            const effectiveLevel = Math.max(1, Math.floor(maxPowerLevel));
            maxPower = parseInt(consular.levelChanges[effectiveLevel]['Max Power Level']);
        }
    }
    return applyTweak(rawCharacter, lowerCase(castingType) + 'Casting.maxPowerLevel', maxPower);
}
function getPowerPoints(rawCharacter, myClasses, myArchetypes, abilityBonus, castingType) {
    const maxPoints = rawCharacter.classes.reduce((acc, { name, archetype, levels }) => {
        const isTech = castingType === 'Tech';
        switch (getMultiplier(name, myClasses, archetype && archetype.name, myArchetypes, castingType)) {
            case 1 / 3:
                return acc + (isTech ? Math.ceil(levels / 2) : levels);
            case 1 / 2:
                return acc + levels * (isTech ? 1 : 2);
            case 2 / 3:
                return acc + levels * 3;
            case 1:
                return acc + levels * (isTech ? 2 : 4);
            default:
                return acc;
        }
    }, abilityBonus);
    return applyTweak(rawCharacter, lowerCase(castingType) + 'Casting.maxPoints', maxPoints);
}
function findPower(power, powers) {
    const powerData = powers.find(({ name }) => name === power);
    if (!powerData)
        console.error('Warning: Power not found: ' + power);
    return powerData;
}
function getPowersKnown(rawCharacter, powers, castingType) {
    return chain(rawCharacter.classes)
        .map(myClass => {
        const powerName = (lowerCase(castingType) + 'Powers');
        const powerList = concat(myClass[powerName] || [], get(myClass, 'archetype.' + powerName) || []);
        return powerList.map(power => findPower(power, powers));
    })
        .concat(rawCharacter[`custom${castingType}Powers`].map(power => findPower(power, powers)))
        .flatten()
        .compact()
        .value();
}
export default function generateCasting(rawCharacter, abilityScores, powers, proficiencyBonus, myClasses, consular, myArchetypes) {
    const techCastingBonus = abilityScores.Intelligence.modifier;
    const techCastingLevel = getCastingLevel(rawCharacter, myClasses, myArchetypes, 'Tech');
    const techCasting = {
        pointsUsed: rawCharacter.currentStats.techPointsUsed,
        maxPoints: getPowerPoints(rawCharacter, myClasses, myArchetypes, techCastingBonus, 'Tech'),
        attackModifier: applyTweak(rawCharacter, 'techCasting.attackModifier', techCastingBonus + proficiencyBonus),
        saveDC: applyTweak(rawCharacter, 'techCasting.saveDC', 8 + techCastingBonus + proficiencyBonus),
        maxPowerLevel: getMaxPowerLevel(rawCharacter, myClasses, consular, myArchetypes, 'Tech'),
        powersKnown: getPowersKnown(rawCharacter, powers, 'Tech')
    };
    const hasTechCasting = techCastingLevel >= 0.6 || techCasting.powersKnown.length > 0;
    const forceCastingBonus = {
        light: abilityScores.Wisdom.modifier,
        dark: abilityScores.Charisma.modifier,
        universal: Math.max(abilityScores.Wisdom.modifier, abilityScores.Charisma.modifier)
    };
    const forceCastingLevel = getCastingLevel(rawCharacter, myClasses, myArchetypes, 'Force');
    const forcePowersKnown = getPowersKnown(rawCharacter, powers, 'Force');
    const forceCasting = {
        pointsUsed: rawCharacter.currentStats.forcePointsUsed,
        maxPoints: getPowerPoints(rawCharacter, myClasses, myArchetypes, forceCastingBonus.universal, 'Force'),
        lightAttackModifier: applyTweak(rawCharacter, 'forceCasting.lightAttackModifier', forceCastingBonus.light + proficiencyBonus),
        lightSaveDC: applyTweak(rawCharacter, 'forceCasting.lightSaveDC', 8 + forceCastingBonus.light + proficiencyBonus),
        darkAttackModifier: applyTweak(rawCharacter, 'forceCasting.darkAttackModifier', forceCastingBonus.dark + proficiencyBonus),
        darkSaveDC: applyTweak(rawCharacter, 'forceCasting.darkSaveDC', 8 + forceCastingBonus.dark + proficiencyBonus),
        universalAttackModifier: applyTweak(rawCharacter, 'forceCasting.universalAttackModifier', forceCastingBonus.universal + proficiencyBonus),
        universalSaveDC: applyTweak(rawCharacter, 'forceCasting.universalSaveDC', 8 + forceCastingBonus.universal + proficiencyBonus),
        maxPowerLevel: getMaxPowerLevel(rawCharacter, myClasses, consular, myArchetypes, 'Force'),
        powersKnown: forcePowersKnown
    };
    const hasForceCasting = forceCastingLevel >= 0.6 || forceCasting.powersKnown.length > 0;
    const allForcePowers = [
        ...rawCharacter.customForcePowers,
        ...rawCharacter.classes.map(({ forcePowers }) => forcePowers || []).flat()
    ];
    return {
        highLevelCasting: rawCharacter.currentStats.highLevelCasting,
        techCasting: hasTechCasting && techCasting,
        forceCasting: hasForceCasting && forceCasting,
        allForcePowers
    };
}
